import { useCallback, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  TrendingUp,
  FileText,
  PoundSterling,
  Package,
  X,
  Upload,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Sparkles,
  TriangleAlert,
  CheckCircle2,
  Info,
  FileSpreadsheet,
  Brain,
  Search,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { parsePrescriptionCSV, calculateFP34Summary, analyseDiscrepancies, type FP34Summary, type InsightSeverity } from "@/lib/fp34CsvParser";
import { AVAILABLE_FP34_REPORTS, type AvailableFp34Report } from "@/lib/sampleFp34Report";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-GB").format(value);
}

// ── Severity config ──
const SEVERITY_CONFIG: Record<InsightSeverity, { icon: typeof AlertCircle; bg: string; bgDark: string; border: string; borderDark: string; text: string; textDark: string; dot: string }> = {
  critical: { icon: AlertCircle, bg: "bg-red-50", bgDark: "bg-red-900/20", border: "border-red-200", borderDark: "border-red-800", text: "text-red-700", textDark: "text-red-300", dot: "bg-red-500" },
  warning: { icon: TriangleAlert, bg: "bg-amber-50", bgDark: "bg-amber-900/20", border: "border-amber-200", borderDark: "border-amber-800", text: "text-amber-700", textDark: "text-amber-300", dot: "bg-amber-500" },
  info: { icon: Info, bg: "bg-blue-50", bgDark: "bg-blue-900/20", border: "border-blue-200", borderDark: "border-blue-800", text: "text-blue-700", textDark: "text-blue-300", dot: "bg-blue-500" },
  success: { icon: CheckCircle2, bg: "bg-green-50", bgDark: "bg-green-900/20", border: "border-green-200", borderDark: "border-green-800", text: "text-green-700", textDark: "text-green-300", dot: "bg-green-500" },
};

// ── AI Analysis View ──
function ImportedDataView({ data, fileName, isDarkMode, onClear }: {
  data: FP34Summary;
  fileName: string;
  isDarkMode: boolean;
  onClear: () => void;
}) {
  const [expandedInsights, setExpandedInsights] = useState<Set<string>>(new Set());
  const [showItems, setShowItems] = useState(false);
  const [itemSort, setItemSort] = useState<{ col: string; dir: "asc" | "desc" }>({ col: "basicPrice", dir: "desc" });
  const [filterSeverity, setFilterSeverity] = useState<InsightSeverity | "all">("all");

  const cardBg = isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const textMuted = isDarkMode ? "text-gray-500" : "text-gray-400";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const rowHover = isDarkMode ? "hover:bg-gray-750" : "hover:bg-gray-50";
  const sectionBg = isDarkMode ? "bg-gray-900/50" : "bg-blue-50/50";
  const totalBg = isDarkMode ? "bg-gray-700/50" : "bg-gray-100";

  const totalDrugReimbursement = data.totalBasicPrice + data.totalConsumables + data.totalContainers;
  const totalAllFees = data.totalProfessionalFees + data.totalExpensiveItemFees + data.totalCdSchedule2Fees +
    data.totalCdSchedule3Fees + data.totalMethadoneFees + data.totalMethadonePckgdDoseFees +
    data.totalSspFees + data.totalHomeDelFees + data.totalStomaFees + data.totalUnlicensedMedsFees;
  const grandTotal = totalDrugReimbursement + totalAllFees;

  // AI Analysis
  const insights = useMemo(() => analyseDiscrepancies(data), [data]);
  const filteredInsights = filterSeverity === "all" ? insights : insights.filter((i) => i.severity === filterSeverity);
  const criticalCount = insights.filter((i) => i.severity === "critical").length;
  const warningCount = insights.filter((i) => i.severity === "warning").length;
  const infoCount = insights.filter((i) => i.severity === "info").length;
  const successCount = insights.filter((i) => i.severity === "success").length;

  const toggleInsight = (id: string) => {
    setExpandedInsights((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Sorted items for drill-down
  const sortedItems = useMemo(() => {
    const items = data.items.filter((i) => i.productDescription && i.productDescription !== "None");
    return [...items].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (itemSort.col) {
        case "basicPrice": aVal = a.basicPrice; bVal = b.basicPrice; break;
        case "product": aVal = a.productDescription; bVal = b.productDescription; break;
        case "quantity": aVal = a.quantity; bVal = b.quantity; break;
        case "profFee": aVal = a.zdrProfFeeValue + a.sdrProfFeeValue; bVal = b.zdrProfFeeValue + b.sdrProfFeeValue; break;
        default: return 0;
      }
      if (typeof aVal === "string") return itemSort.dir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return itemSort.dir === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [data.items, itemSort]);

  const toggleSort = (col: string) => {
    setItemSort((prev) => prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" });
  };

  return (
    <div className="p-6 space-y-6">
      {/* AI Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
            <Sparkles className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <h2 className={`text-sm font-semibold ${textPrimary} flex items-center gap-2`}>
              AI FP34 Analysis
              <span className={`text-[10px] font-normal px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-100 to-indigo-100 text-violet-700 ${isDarkMode ? "from-violet-900/40 to-indigo-900/40 text-violet-300" : ""}`}>
                {formatNumber(data.totalItems)} items analysed
              </span>
            </h2>
            <p className={`text-xs ${textSecondary} mt-0.5`}>
              {data.ocsCode} - {data.dispensingMonthLabel}
              <span className={`ml-2 ${textMuted}`}>{fileName}</span>
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={onClear}>
          <X className="w-3 h-3 mr-1" /> Clear
        </Button>
      </div>

      {/* Severity Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {([
          { severity: "critical" as InsightSeverity, label: "Critical", count: criticalCount, color: "red" },
          { severity: "warning" as InsightSeverity, label: "Warnings", count: warningCount, color: "amber" },
          { severity: "info" as InsightSeverity, label: "Info", count: infoCount, color: "blue" },
          { severity: "success" as InsightSeverity, label: "Passed", count: successCount, color: "green" },
        ]).map((s) => {
          const cfg = SEVERITY_CONFIG[s.severity];
          const isActive = filterSeverity === s.severity;
          return (
            <button
              key={s.severity}
              onClick={() => setFilterSeverity(isActive ? "all" : s.severity)}
              className={`rounded-lg border p-3 text-left transition-all ${
                isActive
                  ? `${isDarkMode ? cfg.borderDark : cfg.border} ${isDarkMode ? cfg.bgDark : cfg.bg} ring-1 ring-offset-1 ${isDarkMode ? "ring-gray-600 ring-offset-gray-900" : `ring-${s.color}-300 ring-offset-white`}`
                  : `${borderColor} ${cardBg} hover:border-gray-300`
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  <span className={`text-[11px] font-medium ${isDarkMode ? cfg.textDark : cfg.text}`}>{s.label}</span>
                </div>
                <span className={`text-lg font-bold ${isDarkMode ? cfg.textDark : cfg.text}`}>{s.count}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* AI Insights */}
      <Card className={`${cardBg} border`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className={`text-xs font-semibold ${textPrimary} flex items-center gap-2`}>
              <Sparkles className="w-3.5 h-3.5 text-violet-500" />
              Discrepancy Analysis & Insights
            </CardTitle>
            {filterSeverity !== "all" && (
              <button onClick={() => setFilterSeverity("all")} className={`text-[10px] ${textSecondary} hover:underline`}>
                Show all
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          {filteredInsights.map((insight) => {
            const cfg = SEVERITY_CONFIG[insight.severity];
            const Icon = cfg.icon;
            const isExpanded = expandedInsights.has(insight.id);

            return (
              <div
                key={insight.id}
                className={`rounded-lg border ${isDarkMode ? cfg.borderDark : cfg.border} ${isDarkMode ? cfg.bgDark : cfg.bg} overflow-hidden`}
              >
                <button
                  onClick={() => toggleInsight(insight.id)}
                  className="flex items-start gap-3 p-3 w-full text-left"
                >
                  <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${isDarkMode ? cfg.textDark : cfg.text}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-white/60 text-gray-600"}`}>
                          {insight.category}
                        </span>
                        <span className={`text-xs font-medium ${isDarkMode ? cfg.textDark : cfg.text} truncate`}>
                          {insight.title}
                        </span>
                      </div>
                      {insight.impact && (
                        <span className={`text-[10px] font-semibold shrink-0 ${isDarkMode ? cfg.textDark : cfg.text}`}>
                          {insight.impact}
                        </span>
                      )}
                    </div>
                    <p className={`text-[11px] mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"} line-clamp-2`}>
                      {insight.description}
                    </p>
                  </div>
                  {insight.items && insight.items.length > 0 && (
                    <div className="shrink-0">
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                    </div>
                  )}
                </button>
                {isExpanded && insight.items && insight.items.length > 0 && (
                  <div className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200/60"} px-3 pb-3`}>
                    <div className="mt-2 space-y-1">
                      {insight.items.map((item, idx) => (
                        <div key={idx} className={`flex items-center justify-between py-1.5 px-2 rounded text-[11px] ${isDarkMode ? "bg-gray-800/50" : "bg-white/50"}`}>
                          <span className={`font-medium ${textPrimary} truncate max-w-[60%]`}>{item.description}</span>
                          <span className={textMuted}>{item.detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Financial Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { icon: FileText, label: "Total Forms", value: formatNumber(data.totalForms) },
          { icon: Package, label: "Total Items", value: formatNumber(data.totalItems) },
          { icon: PoundSterling, label: "Net Ingredient Cost", value: formatCurrency(data.totalBasicPrice) },
          { icon: PoundSterling, label: "Professional Fees", value: formatCurrency(data.totalProfessionalFees) },
          { icon: TrendingUp, label: "Total Reimbursement", value: formatCurrency(grandTotal), highlight: true },
        ].map((kpi) => (
          <div key={kpi.label} className={`rounded-lg border ${borderColor} ${cardBg} p-4`}>
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon className={`w-4 h-4 ${kpi.highlight ? "text-green-500" : textSecondary}`} />
              <span className={`text-xs ${textSecondary}`}>{kpi.label}</span>
            </div>
            <p className={`text-lg font-semibold ${kpi.highlight ? "text-green-600" : textPrimary}`}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* FP34 Reconciliation Table */}
      <Card className={`${cardBg} border`}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-sm font-semibold ${textPrimary}`}>
            FP34 Reconciliation - {data.dispensingMonthLabel} ({data.ocsCode})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              {/* Part 1 */}
              <thead>
                <tr className={sectionBg}>
                  <th className={`text-left px-4 py-2 font-semibold ${textPrimary} border-b ${borderColor}`} colSpan={3}>Part 1 - Prescription Forms & Items</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Charge not payable (exempt)", forms: data.noChargeForms, items: data.noChargeItems },
                  { label: "Charge paid by patient", forms: data.chargePaidForms, items: data.chargePaidItems },
                ].map((row) => (
                  <tr key={row.label} className={`border-b ${borderColor} ${rowHover}`}>
                    <td className={`px-4 py-2 ${textPrimary}`}>{row.label}</td>
                    <td className={`text-right px-4 py-2 ${textPrimary} font-medium`}>{formatNumber(row.forms)}</td>
                    <td className={`text-right px-4 py-2 ${textPrimary} font-medium`}>{formatNumber(row.items)}</td>
                  </tr>
                ))}
                <tr className={`${totalBg} border-b ${borderColor}`}>
                  <td className={`px-4 py-2 font-semibold ${textPrimary}`}>Total</td>
                  <td className={`text-right px-4 py-2 font-semibold ${textPrimary}`}>{formatNumber(data.totalForms)}</td>
                  <td className={`text-right px-4 py-2 font-semibold ${textPrimary}`}>{formatNumber(data.totalItems)}</td>
                </tr>
                <tr className={`border-b ${borderColor} ${rowHover}`}>
                  <td className={`px-4 py-2 ${textSecondary} pl-6`}>EPS items</td>
                  <td className={`text-right px-4 py-2 ${textSecondary}`}>{formatNumber(data.epsForms)}</td>
                  <td className={`text-right px-4 py-2 ${textSecondary}`}>{formatNumber(data.epsItems)}</td>
                </tr>
                <tr className={`border-b ${borderColor} ${rowHover}`}>
                  <td className={`px-4 py-2 ${textSecondary} pl-6`}>FP10 (Paper)</td>
                  <td className={`text-right px-4 py-2 ${textSecondary}`}>{formatNumber(data.paperForms)}</td>
                  <td className={`text-right px-4 py-2 ${textSecondary}`}>{formatNumber(data.paperItems)}</td>
                </tr>
              </tbody>

              {/* Part 2 - Drug Costs */}
              <thead><tr className={sectionBg}><th className={`text-left px-4 py-2 font-semibold ${textPrimary} border-b ${borderColor}`} colSpan={3}>Part 2 - Drug Costs & Reimbursement</th></tr></thead>
              <tbody>
                {[
                  { label: "Net Ingredient Cost (Basic Price)", value: data.totalBasicPrice },
                  { label: "Payment for Consumables", value: data.totalConsumables },
                  { label: "Payment for Containers", value: data.totalContainers },
                ].map((row) => (
                  <tr key={row.label} className={`border-b ${borderColor} ${rowHover}`}>
                    <td className={`px-4 py-2 ${textPrimary}`}>{row.label}</td>
                    <td className={`text-right px-4 py-2 ${textPrimary} font-medium`} colSpan={2}>{formatCurrency(row.value)}</td>
                  </tr>
                ))}
                <tr className={`${totalBg} border-b ${borderColor}`}>
                  <td className={`px-4 py-2 font-semibold ${textPrimary}`}>Sub-total (Drug Reimbursement)</td>
                  <td className={`text-right px-4 py-2 font-semibold ${textPrimary}`} colSpan={2}>{formatCurrency(totalDrugReimbursement)}</td>
                </tr>
              </tbody>

              {/* Part 3 - Fees */}
              <thead><tr className={sectionBg}><th className={`text-left px-4 py-2 font-semibold ${textPrimary} border-b ${borderColor}`} colSpan={3}>Part 3 - Professional & Dispensing Fees</th></tr></thead>
              <tbody>
                {[
                  { label: "SDR Professional Fee (Paper)", value: data.totalSdrProfFees },
                  { label: "ZDR Professional Fee (EPS)", value: data.totalZdrProfFees },
                  { label: "Expensive Item Fee", value: data.totalExpensiveItemFees },
                  { label: "CD Schedule 2 Fee", value: data.totalCdSchedule2Fees },
                  { label: "CD Schedule 3 Fee", value: data.totalCdSchedule3Fees },
                  { label: "Methadone Fee", value: data.totalMethadoneFees },
                  { label: "Methadone Packaged Dose Fee", value: data.totalMethadonePckgdDoseFees },
                  { label: "SSP Fee", value: data.totalSspFees },
                  { label: "Home Delivery Appliance Fee", value: data.totalHomeDelFees },
                  { label: "Stoma Customisation Fee", value: data.totalStomaFees },
                  { label: "Unlicensed Medicines Fee", value: data.totalUnlicensedMedsFees },
                ].filter((r) => r.value > 0).map((row) => (
                  <tr key={row.label} className={`border-b ${borderColor} ${rowHover}`}>
                    <td className={`px-4 py-2 ${textPrimary}`}>{row.label}</td>
                    <td className={`text-right px-4 py-2 ${textPrimary} font-medium`} colSpan={2}>{formatCurrency(row.value)}</td>
                  </tr>
                ))}
                <tr className={`${totalBg} border-b ${borderColor}`}>
                  <td className={`px-4 py-2 font-semibold ${textPrimary}`}>Total Fees</td>
                  <td className={`text-right px-4 py-2 font-semibold ${textPrimary}`} colSpan={2}>{formatCurrency(totalAllFees)}</td>
                </tr>
              </tbody>

              {/* Grand Total */}
              <tbody>
                <tr className={`${isDarkMode ? "bg-green-900/20" : "bg-green-50"}`}>
                  <td className={`px-4 py-3 font-bold ${textPrimary}`}>Grand Total</td>
                  <td className="text-right px-4 py-3 font-bold text-green-600 text-sm" colSpan={2}>{formatCurrency(grandTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Products */}
      {data.topProducts.length > 0 && (
        <Card className={`${cardBg} border`}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-xs font-semibold ${textPrimary}`}>Top Products by NIC Value</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className={`border-b ${borderColor}`}>
                  <th className={`text-left px-4 py-2 font-medium ${textSecondary}`}>Product</th>
                  <th className={`text-right px-4 py-2 font-medium ${textSecondary}`}>Items</th>
                  <th className={`text-right px-4 py-2 font-medium ${textSecondary}`}>Total NIC</th>
                </tr></thead>
                <tbody>
                  {data.topProducts.map((p, i) => (
                    <tr key={i} className={`border-b ${borderColor} ${rowHover}`}>
                      <td className={`px-4 py-2 ${textPrimary} max-w-[300px] truncate`}>{p.description}</td>
                      <td className={`text-right px-4 py-2 ${textSecondary}`}>{p.items}</td>
                      <td className={`text-right px-4 py-2 ${textPrimary} font-medium`}>{formatCurrency(p.totalValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Item Drill-Down */}
      <Card className={`${cardBg} border`}>
        <CardHeader className="pb-2">
          <button onClick={() => setShowItems(!showItems)} className="flex items-center gap-2 w-full">
            {showItems ? <ChevronDown className={`w-4 h-4 ${textSecondary}`} /> : <ChevronRight className={`w-4 h-4 ${textSecondary}`} />}
            <CardTitle className={`text-xs font-semibold ${textPrimary}`}>Prescription Item Detail ({formatNumber(sortedItems.length)} items)</CardTitle>
          </button>
        </CardHeader>
        {showItems && (
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className={`${isDarkMode ? "bg-gray-800" : "bg-white"} border-b ${borderColor}`}>
                    <th className={`text-left px-4 py-2 font-medium ${textSecondary}`}>#</th>
                    <th className={`text-left px-4 py-2 font-medium ${textSecondary} cursor-pointer`} onClick={() => toggleSort("product")}>Product {itemSort.col === "product" ? (itemSort.dir === "asc" ? "↑" : "↓") : ""}</th>
                    <th className={`text-right px-4 py-2 font-medium ${textSecondary} cursor-pointer`} onClick={() => toggleSort("quantity")}>Qty {itemSort.col === "quantity" ? (itemSort.dir === "asc" ? "↑" : "↓") : ""}</th>
                    <th className={`text-right px-4 py-2 font-medium ${textSecondary}`}>UoM</th>
                    <th className={`text-right px-4 py-2 font-medium ${textSecondary} cursor-pointer`} onClick={() => toggleSort("basicPrice")}>NIC {itemSort.col === "basicPrice" ? (itemSort.dir === "asc" ? "↑" : "↓") : ""}</th>
                    <th className={`text-right px-4 py-2 font-medium ${textSecondary} cursor-pointer`} onClick={() => toggleSort("profFee")}>Prof Fee {itemSort.col === "profFee" ? (itemSort.dir === "asc" ? "↑" : "↓") : ""}</th>
                    <th className={`text-right px-4 py-2 font-medium ${textSecondary}`}>Form</th>
                    <th className={`text-right px-4 py-2 font-medium ${textSecondary}`}>Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, i) => (
                    <tr key={i} className={`border-b ${borderColor} ${rowHover}`}>
                      <td className={`px-4 py-1.5 ${textMuted}`}>{i + 1}</td>
                      <td className={`px-4 py-1.5 ${textPrimary} max-w-[250px] truncate`}>{item.productDescription}</td>
                      <td className={`text-right px-4 py-1.5 ${textSecondary}`}>{item.quantity}</td>
                      <td className={`text-right px-4 py-1.5 ${textMuted}`}>{item.unitOfMeasure}</td>
                      <td className={`text-right px-4 py-1.5 ${textPrimary} font-medium`}>{formatCurrency(item.basicPrice)}</td>
                      <td className={`text-right px-4 py-1.5 ${textSecondary}`}>{formatCurrency(item.zdrProfFeeValue + item.sdrProfFeeValue)}</td>
                      <td className={`text-right px-4 py-1.5 ${textMuted}`}>{item.formType || "EPS"}</td>
                      <td className="text-right px-4 py-1.5">
                        {item.chargesPayable > 0 ? <span className="text-amber-500">Paid</span> : <span className={textMuted}>Exempt</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

// ── Hook: encapsulates the full AI FP34 Analyser feature ──
export function useAiFp34Analyser(isDarkMode: boolean) {
  const [importedData, setImportedData] = useState<FP34Summary | null>(null);
  const [importFileName, setImportFileName] = useState<string>("");
  const [importError, setImportError] = useState<string>("");
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiStage, setAiStage] = useState<"intro" | "processing">("intro");
  const [aiProgress, setAiProgress] = useState(0);
  const [aiStatusMessage, setAiStatusMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const runProcessingSimulation = useCallback((onComplete: () => void) => {
    const stages = [
      { pct: 12, msg: "Reading file…" },
      { pct: 28, msg: "Parsing prescription items…" },
      { pct: 46, msg: "Reconciling against dispensed claims…" },
      { pct: 64, msg: "Calculating FP34 schedule of payments…" },
      { pct: 82, msg: "Running AI discrepancy detection…" },
      { pct: 96, msg: "Generating insights…" },
    ];
    let i = 0;
    const tick = () => {
      if (i >= stages.length) {
        setAiProgress(100);
        setAiStatusMessage("Analysis complete");
        setTimeout(onComplete, 400);
        return;
      }
      setAiProgress(stages[i].pct);
      setAiStatusMessage(stages[i].msg);
      i++;
      setTimeout(tick, 550 + Math.random() * 300);
    };
    tick();
  }, []);

  const processCsvText = useCallback((text: string, fileName: string) => {
    setImportError("");
    setImportFileName(fileName);
    setAiStage("processing");
    setAiProgress(0);
    setAiStatusMessage("Starting…");

    runProcessingSimulation(() => {
      try {
        const items = parsePrescriptionCSV(text);
        if (items.length === 0) {
          setImportError("No prescription items found in the file. Please check the format matches an NHS Prescription Item Report.");
          setAiDialogOpen(false);
          setAiStage("intro");
          return;
        }
        const summary = calculateFP34Summary(items);
        setImportedData(summary);
        setAiDialogOpen(false);
        setAiStage("intro");
      } catch (err) {
        setImportError("Failed to parse the file. Please ensure it is a valid NHS Prescription Item Report.");
        setAiDialogOpen(false);
        setAiStage("intro");
      }
    });
  }, [runProcessingSimulation]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fileName = file.name;
    const reader = new FileReader();
    reader.onload = (evt) => {
      processCsvText(evt.target?.result as string, fileName);
    };
    reader.readAsText(file);
    // Reset input so same file can be re-uploaded
    e.target.value = "";
  }, [processCsvText]);

  const handleSelectReport = useCallback((report: AvailableFp34Report) => {
    processCsvText(report.csv, report.fileName);
  }, [processCsvText]);

  const openDialog = useCallback(() => {
    setAiStage("intro");
    setAiProgress(0);
    setAiStatusMessage("");
    setAiDialogOpen(true);
  }, []);

  const clear = useCallback(() => {
    setImportedData(null);
    setImportFileName("");
  }, []);

  const trigger = (
    <button
      onClick={openDialog}
      className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shadow-sm shadow-violet-500/20 transition-all duration-200 hover:shadow-md hover:shadow-violet-500/30"
    >
      <Sparkles className="w-3.5 h-3.5" />
      AI FP34 Analyser
    </button>
  );

  const errorBanner = importError ? (
    <div className={`flex items-center gap-2 p-3 rounded-lg border ${isDarkMode ? "border-red-800 bg-red-900/20" : "border-red-200 bg-red-50"}`}>
      <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
      <span className={`text-xs ${isDarkMode ? "text-red-300" : "text-red-700"}`}>{importError}</span>
      <button onClick={() => setImportError("")} className="ml-auto"><X className="w-3 h-3 text-red-400" /></button>
    </div>
  ) : null;

  const overlay = (
    <>
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept=".csv,text/csv,text/plain,application/vnd.ms-excel,application/csv" className="hidden" onChange={handleFileUpload} />

      {/* AI FP34 Analyser Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={(open) => {
        // Prevent closing while processing
        if (aiStage === "processing" && !open) return;
        setAiDialogOpen(open);
        if (!open) setAiStage("intro");
      }}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          {/* Gradient header */}
          <div className="relative bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-600 px-6 pt-6 pb-5 text-white">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.4),_transparent_50%)]" />
            <div className="relative flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/20">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-base font-semibold tracking-tight">AI FP34 Analyser</DialogTitle>
                <DialogDescription className="text-[11px] text-white/80 mt-0.5">
                  Reconcile your FP34 file with dispensed data and uncover discrepancies
                </DialogDescription>
              </div>
            </div>
          </div>

          {aiStage === "intro" && (
            <div className="px-6 py-5 space-y-5">
              <p className="text-xs text-gray-600 leading-relaxed">
                Upload your <strong>NHS Prescription Item Report</strong> (the CSV/file you receive
                from the NHSBSA each month). Our AI will parse every line item, rebuild your FP34
                Schedule of Payments, and compare it line-by-line against your dispensed claims to
                surface any missing fees, mis-endorsements, or reimbursement gaps.
              </p>

              {/* Feature list */}
              <div className="grid grid-cols-1 gap-2.5">
                {[
                  { icon: FileSpreadsheet, title: "Parses your full prescription item report", desc: "Reads every dispensed item, fees, charges and endorsements." },
                  { icon: Search, title: "Reconciles against your dispensed data", desc: "Spots mismatches between what you claimed and what was paid." },
                  { icon: Brain, title: "AI-driven discrepancy detection", desc: "Highlights missing prof fees, broken bulks, CD fees, expensive items and more." },
                  { icon: ShieldCheck, title: "Private & on-device", desc: "Your file is processed locally in your browser — nothing is uploaded." },
                ].map((f) => (
                  <div key={f.title} className="flex gap-3 p-2.5 rounded-lg border border-gray-100 bg-gray-50/60">
                    <div className="w-7 h-7 rounded-md bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center shrink-0">
                      <f.icon className="w-3.5 h-3.5 text-violet-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium text-gray-900">{f.title}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5 leading-snug">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Available auto-pulled reports */}
              {AVAILABLE_FP34_REPORTS.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[11px] font-medium text-gray-900">Select a report already pulled from the NHSBSA</p>
                  <div className="grid grid-cols-1 gap-2">
                    {AVAILABLE_FP34_REPORTS.map((report) => (
                      <button
                        key={report.id}
                        onClick={() => handleSelectReport(report)}
                        className="group flex items-center gap-3 w-full text-left rounded-xl border border-violet-200 hover:border-violet-400 bg-white hover:bg-violet-50/60 p-3 transition-all"
                      >
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center shrink-0">
                          <FileSpreadsheet className="w-4 h-4 text-violet-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium text-gray-900 truncate">{report.fileName}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 truncate">
                            {report.ocsCode} · {report.dispensingMonth} · {report.pulledAt}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-violet-500 shrink-0 transition-colors" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-[10px] text-gray-400 uppercase tracking-wide">or</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              {/* Upload area */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full group relative rounded-xl border-2 border-dashed border-violet-200 hover:border-violet-400 bg-gradient-to-br from-violet-50/40 to-indigo-50/40 hover:from-violet-50 hover:to-indigo-50 p-5 transition-all"
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-violet-100 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Upload className="w-4 h-4 text-violet-600" />
                  </div>
                  <p className="text-xs font-medium text-gray-900">Upload your FP34 file</p>
                  <p className="text-[10px] text-gray-500">Click to browse — accepts .csv from NHSBSA</p>
                </div>
              </button>

              <p className="text-[10px] text-gray-400 text-center">
                Processing usually takes a few seconds depending on file size.
              </p>
            </div>
          )}

          {aiStage === "processing" && (
            <div className="px-6 py-8 space-y-6">
              <div className="flex flex-col items-center text-center gap-3">
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                    <Brain className="w-7 h-7 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-md">
                    <Loader2 className="w-3.5 h-3.5 text-violet-600 animate-spin" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Analysing your FP34 file</p>
                  <p className="text-[11px] text-gray-500 mt-0.5 truncate max-w-[320px]">{importFileName}</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${aiProgress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-gray-600 flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin text-violet-500" />
                    {aiStatusMessage}
                  </span>
                  <span className="font-medium text-violet-600">{aiProgress}%</span>
                </div>
              </div>

              {/* Step indicators */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Parse", threshold: 28 },
                  { label: "Reconcile", threshold: 64 },
                  { label: "Insights", threshold: 96 },
                ].map((s) => {
                  const done = aiProgress >= s.threshold;
                  return (
                    <div
                      key={s.label}
                      className={`rounded-lg border p-2 text-center transition-all ${
                        done ? "border-violet-200 bg-violet-50" : "border-gray-100 bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center mb-1">
                        {done ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-violet-600" />
                        ) : (
                          <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                        )}
                      </div>
                      <p className={`text-[10px] font-medium ${done ? "text-violet-700" : "text-gray-500"}`}>
                        {s.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  const results = importedData ? (
    <ImportedDataView data={importedData} fileName={importFileName} isDarkMode={isDarkMode} onClear={clear} />
  ) : null;

  return { trigger, overlay, errorBanner, results, hasImportedData: !!importedData, openDialog, clear };
}
